import React from 'react';
import { Card, CardContent, Typography, Box } from '@mui/material';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  ReferenceLine,
  Cell,
} from 'recharts';
import { OnboardingEntry } from '../../types/franchise';

interface TimeToFirstDollarChartProps {
  data: OnboardingEntry[];
}

const CustomTooltip = ({ active, payload }: any) => {
  if (!active || !payload?.length) return null;
  const entry = payload[0].payload as OnboardingEntry;
  return (
    <Box
      sx={{
        bgcolor: 'background.paper',
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 1,
        p: 1.5,
        boxShadow: 2,
      }}
    >
      <Typography variant="caption" sx={{ fontWeight: 700, display: 'block', mb: 0.5 }}>
        {entry.franchisee}
      </Typography>
      <Typography variant="caption" sx={{ display: 'block' }}>
        Days to first revenue: <strong>{entry.days}</strong>
      </Typography>
      <Typography variant="caption" sx={{ display: 'block', color: entry.days <= entry.target ? '#2E7D32' : '#D32F2F' }}>
        {entry.days <= entry.target ? 'Under target' : `${entry.days - entry.target} days over target`}
      </Typography>
    </Box>
  );
};

export const TimeToFirstDollarChart: React.FC<TimeToFirstDollarChartProps> = ({ data }) => {
  const targetDays = data[0]?.target || 21;

  return (
    <Card sx={{ height: '100%' }}>
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
          <Typography variant="h6" sx={{ fontWeight: 700, fontSize: '1rem' }}>
            Time to First Dollar
          </Typography>
          <Typography variant="caption" sx={{ color: 'text.secondary' }}>
            Last 5 onboarded
          </Typography>
        </Box>
        <ResponsiveContainer width="100%" height={280}>
          <BarChart
            data={data}
            layout="vertical"
            margin={{ top: 5, right: 30, left: 80, bottom: 5 }}
          >
            <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" horizontal={false} />
            <XAxis
              type="number"
              tick={{ fontSize: 12, fill: '#666' }}
              axisLine={{ stroke: '#e0e0e0' }}
              label={{ value: 'Days', position: 'insideBottomRight', offset: -5, fontSize: 11, fill: '#999' }}
            />
            <YAxis
              type="category"
              dataKey="franchisee"
              tick={{ fontSize: 12, fill: '#444' }}
              axisLine={{ stroke: '#e0e0e0' }}
              width={75}
            />
            <Tooltip content={<CustomTooltip />} />
            <ReferenceLine
              x={targetDays}
              stroke="#F57C00"
              strokeDasharray="6 4"
              label={{
                value: `Target: ${targetDays}d`,
                position: 'top',
                fill: '#F57C00',
                fontSize: 11,
                fontWeight: 600,
              }}
            />
            <Bar dataKey="days" radius={[0, 6, 6, 0]} barSize={28}>
              {data.map((entry, index) => (
                <Cell
                  key={index}
                  fill={entry.days <= entry.target ? '#2E7D32' : entry.brandColor}
                  opacity={entry.days <= entry.target ? 0.85 : 0.9}
                />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </CardContent>
    </Card>
  );
};
